import React, { useEffect, useRef } from 'react';
import { useToastStore, type Toast } from '../../stores/toast-store';

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconError() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconSuccess() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none">
      <circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── ToastItem ─────────────────────────────────────────────────────────────────

const STYLES = {
  error: {
    bar:  'bg-rose',
    icon: 'text-rose',
    Icon: IconError,
  },
  success: {
    bar:  'bg-emerald',
    icon: 'text-emerald',
    Icon: IconSuccess,
  },
  warning: {
    bar:  'bg-amber',
    icon: 'text-amber',
    Icon: IconWarning,
  },
};

function ToastItem({ toast }: { toast: Toast }) {
  const { dismiss } = useToastStore();
  const { bar, icon, Icon } = STYLES[toast.type];
  const ref = useRef<HTMLDivElement>(null);

  // Animate in
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 200ms ease, transform 200ms ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  return (
    <div
      ref={ref}
      className="flex items-start gap-3 w-full max-w-sm bg-surface-card border border-stroke-hi rounded-xl shadow-2xl overflow-hidden"
      role="alert"
    >
      {/* Colored left bar */}
      <div className={`w-1 self-stretch flex-none ${bar}`} />

      {/* Icon + message */}
      <div className={`pt-3 pb-1 flex-none ${icon}`}>
        <Icon />
      </div>
      <p className="flex-1 py-3 pr-1 text-sm text-ink leading-snug">
        {toast.message}
      </p>

      {/* Dismiss */}
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="p-3 text-ink-muted hover:text-ink transition-colors flex-none"
        aria-label="Cerrar"
      >
        <IconClose />
      </button>
    </div>
  );
}

// ── ToastContainer ────────────────────────────────────────────────────────────

export function ToastContainer() {
  const { toasts } = useToastStore();
  if (toasts.length === 0) return null;

  return (
    // bottom-20 on mobile (above bottom nav h-16), bottom-4 on sm+
    <div className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 z-[200] flex flex-col gap-2 items-center sm:items-end w-[calc(100vw-2rem)] sm:w-auto">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
